import React, { useState, useCallback, useRef } from "react";
import "../styles/Toast.css";

export const ToastContext = React.createContext();

export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const showToast = useCallback(
    (message, type = "success", duration = 3000) => {
      const id = idRef.current++;
      setToasts((prev) => [...prev, { id, message, type }]);

      if (duration > 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, duration);
      }

      return id;
    },
    []
  );

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const success = (message, duration = 3000) =>
    showToast(message, "success", duration);
  const error = (message, duration = 4000) =>
    showToast(message, "error", duration);
  const info = (message, duration = 3000) =>
    showToast(message, "info", duration);

  return (
    <ToastContext.Provider value={{ showToast, removeToast, success, error, info }}>
      {children}
      <div className="toast-container">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast-${toast.type}`}>
            <div className="toast-content">
              {toast.type === "success" && <span className="toast-icon">✓</span>}
              {toast.type === "error" && <span className="toast-icon">✕</span>}
              {toast.type === "info" && <span className="toast-icon">ℹ</span>}
              <span className="toast-message">{toast.message}</span>
            </div>
            <button
              className="toast-close"
              onClick={() => removeToast(toast.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
